import React from 'react';
import { 
  Shield, CheckCircle, FileCheck, BadgeCheck, 
  Award, Star, Verified, TrendingUp
} from 'lucide-react';

// Badge definitions
const BADGE_CONFIG = {
  mc_verified: {
    icon: FileCheck,
    label: 'MC Verified',
    color: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    description: 'Motor Carrier number verified'
  },
  dot_verified: {
    icon: Shield,
    label: 'DOT Verified',
    color: 'bg-green-500/20 text-green-400 border-green-500/30',
    description: 'DOT number verified'
  },
  insured: {
    icon: BadgeCheck,
    label: 'Insured',
    color: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    description: 'Insurance coverage verified'
  },
  authority_active: {
    icon: Verified,
    label: 'Active Authority',
    color: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    description: 'Operating authority is active'
  },
  registered: {
    icon: CheckCircle,
    label: 'Registered',
    color: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
    description: 'Registered on platform'
  },
  top_rated: {
    icon: Star,
    label: 'Top Rated',
    color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    description: '4.5+ average rating'
  },
  veteran: {
    icon: Award,
    label: 'Veteran',
    color: 'bg-red-500/20 text-red-400 border-red-500/30',
    description: '50+ completed loads'
  },
  rising_star: {
    icon: TrendingUp,
    label: 'Rising Star',
    color: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
    description: 'Fast growing reputation'
  }
};

// Single badge component
export const Badge = ({ type, size = 'sm', showLabel = true, showTooltip = true }) => {
  const config = BADGE_CONFIG[type];
  if (!config) return null;
  
  const Icon = config.icon;
  const sizeClasses = {
    xs: 'w-4 h-4',
    sm: 'w-5 h-5',
    md: 'w-6 h-6',
    lg: 'w-8 h-8'
  };
  
  const paddingClasses = {
    xs: 'px-1.5 py-0.5',
    sm: 'px-2 py-1',
    md: 'px-3 py-1.5',
    lg: 'px-4 py-2'
  };
  
  const textClasses = {
    xs: 'text-[10px]',
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-base'
  };
  
  return (
    <div 
      className={`inline-flex items-center gap-1 rounded-full border ${config.color} ${paddingClasses[size]} group relative`}
      data-testid={`badge-${type}`}
    >
      <Icon className={sizeClasses[size]} />
      {showLabel && <span className={`font-medium ${textClasses[size]}`}>{config.label}</span>}
      
      {showTooltip && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-slate-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-50">
          {config.description}
        </div>
      )}
    </div>
  );
};

// Badge list component
export const DriverBadges = ({ badges = [], size = 'sm', maxShow = 5, className = '' }) => {
  if (!badges || badges.length === 0) {
    return (
      <span className="text-xs text-gray-500 italic">Not verified</span>
    );
  }
  
  const displayBadges = badges.slice(0, maxShow);
  const remaining = badges.length - maxShow;
  
  return (
    <div className={`flex flex-wrap items-center gap-1 ${className}`} data-testid="driver-badges">
      {displayBadges.map(badge => (
        <Badge key={badge} type={badge} size={size} />
      ))}
      {remaining > 0 && (
        <span className="text-xs text-gray-400 ml-1">+{remaining} more</span>
      )}
    </div>
  );
};

// Verification status indicator
export const VerificationStatus = ({ isVerified, verifiedAt, size = 'md' }) => {
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6'
  };
  
  if (isVerified) {
    return (
      <div className="flex items-center gap-2" data-testid="verification-status">
        <div className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-green-500/20 text-green-400">
          <Verified className={sizeClasses[size]} />
          <span className="text-xs font-medium">Verified</span>
        </div>
        {verifiedAt && (
          <span className="text-xs text-gray-500">
            {new Date(verifiedAt).toLocaleDateString()}
          </span>
        )}
      </div>
    );
  }
  
  return (
    <div className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-slate-600/20 text-gray-400" data-testid="verification-status">
      <Shield className={sizeClasses[size]} />
      <span className="text-xs font-medium">Unverified</span>
    </div>
  );
};

// Compact badge icon row (for tables/lists)
export const BadgeIcons = ({ badges = [], size = 'sm' }) => {
  if (!badges || badges.length === 0) return null;
  
  const sizeClasses = {
    xs: 'w-3 h-3',
    sm: 'w-4 h-4',
    md: 'w-5 h-5'
  };
  
  return (
    <div className="flex items-center gap-0.5" data-testid="badge-icons">
      {badges.slice(0, 4).map(badge => {
        const config = BADGE_CONFIG[badge];
        if (!config) return null;
        const Icon = config.icon;
        return (
          <div 
            key={badge}
            className={`p-0.5 rounded ${config.color}`}
            title={config.label}
          >
            <Icon className={sizeClasses[size]} />
          </div>
        );
      })}
    </div>
  );
};

export default DriverBadges;
